import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as constants from "../../constants/CONSTANT";
import FeeReceipt from "../fee/FeeReceipt";
import ReportCardView from "../reportCard/ReportCardView";
import html2pdf from "html2pdf.js";

/**
 * Public, read-only page for a share link: /view/:token   (no login, no sidebar)
 * Opens a fee receipt or a published report card. Used by the web "Share / View" buttons
 * and by the teacher & student apps (they open this URL in the browser).
 */
const PublicShareView = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${constants.API_BASE_URL}/public/share/${encodeURIComponent(token)}`);
        const json = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok || !json.success) setState({ loading: false, error: json.message || "This link is not valid", data: null });
        else setState({ loading: false, error: "", data: json });
      } catch (e) {
        if (alive) setState({ loading: false, error: "Could not load. Please check your internet connection.", data: null });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const data = state.data;

  // data source for FeeReceipt built from the share payload (no login API calls)
  const receiptApi = useMemo(() => {
    if (!data || data.type !== "receipt") return null;
    return {
      tenantcode: data.tenantcode,
      fetchFeeDepositsByStudentId: async (_adm, _session, depositId) => (depositId ? data.current : data.all),
      getallIntsallment: async () => ({ success: true, result: data.installments || [] }),
      getSessionById: async () => ({ success: true, result: data.session || {} }),
    };
  }, [data]);

  const receiptData = useMemo(() => {
    const cur = data?.current?.[0]?.result?.[0] || data?.current?.[0] || {};
    return data?.type === "receipt"
      ? { deposit_id: cur.deposit_id || cur.id || "x", student_addmission_id: cur.student_addmission_id || "x", sessionid: cur.sessionid }
      : null;
  }, [data]);

  // real PDF file download – works in phones / in-app browsers where window.print() does nothing
  const savePdf = async () => {
    if (!printRef.current) return;
    setSaving(true);
    try {
      const name = data?.type === "receipt" ? "Fee_Receipt" : "Report_Card";
      await html2pdf()
        .set({
          margin: 6,
          filename: `${name}.pdf`,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(printRef.current)
        .save();
    } catch (e) {
      window.print();
    } finally {
      setSaving(false);
    }
  };

  const page = { minHeight: "100vh", background: "#f3f4f6", padding: "12px 8px" };
  const box = { maxWidth: 900, margin: "0 auto", background: "#fff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,.08)" };

  if (state.loading) {
    return <div style={page}><div style={{ ...box, textAlign: "center", padding: 40 }}>Loading…</div></div>;
  }
  if (state.error) {
    return (
      <div style={page}>
        <div style={{ ...box, textAlign: "center", padding: 40 }}>
          <i className="fa-solid fa-link-slash fa-2x text-muted mb-3" />
          <h5>{state.error}</h5>
          <p className="text-muted small mb-0">Please ask the school for a new link.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={page} className="public-share-view">
      <style>{`
        @media print { .no-print { display:none !important; } .public-share-view { background:#fff !important; padding:0 !important; } }
        @media (max-width: 768px) {
          .public-share-view { padding: 4px !important; }
          .public-share-box { padding: 6px !important; border-radius: 6px !important; margin-bottom: 6px !important; }
        }
      `}</style>
      <div style={{ ...box, marginBottom: 8 }} className="public-share-box no-print d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <strong>{data.school?.name}</strong>
          <div className="small text-muted">
            {data.type === "receipt" ? "Fee Receipt" : "Report Card"} · link valid till {new Date(data.expires_at).toLocaleDateString("en-GB")}
          </div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-primary" onClick={savePdf} disabled={saving || (data.type === "receipt" && !ready)}>
            <i className="fa-solid fa-file-arrow-down me-1" />{saving ? "Preparing…" : "Download PDF"}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => window.print()} disabled={data.type === "receipt" && !ready}>
            <i className="fa-solid fa-print me-1" />Print
          </button>
        </div>
      </div>

      <div style={box} className="public-share-box" ref={printRef}>
        {data.type === "receipt" && receiptApi && (
          <FeeReceipt receiptData={receiptData} api={receiptApi} onReady={() => setReady(true)} />
        )}
        {data.type === "result" && data.report && (
          <ReportCardView report={data.report} school={data.school || {}} />
        )}
      </div>
    </div>
  );
};

export default PublicShareView;
